import type {
  BorderColorModifierValue,
  BorderStyleModifierValue,
  BorderWidthModifierValue,
} from "schemas/generated/fileModifiers";

import React from "react";

import { useOptimisticModifierValue } from "@v2/features/modifiers/hooks/useOptimisticModifierValue";
import { COMPUTE_VALUE } from "schemas/fileModifiers";

import BorderColorModifier from "./BorderColorModifier";
import BorderStyleModifier from "./BorderStyleModifier";
import BorderWidthModifier from "./BorderWidthModifier";

interface BorderModifierProps {
  onChange: (
    modifiers: (
      | BorderColorModifierValue
      | BorderStyleModifierValue
      | BorderWidthModifierValue
    )[],
  ) => void;
}

const BorderModifier: React.FC<BorderModifierProps> = ({ onChange }) => {
  // NOTE (Kevin, 2025-12-19, REPL-21177): Track current border widths to detect 0→non-zero transitions
  const currentBorderTopWidth =
    useOptimisticModifierValue({
      modifierType: { type: "borderWidth", direction: "top", value: 0 },
      extractor: (elementDesignation) => {
        return (
          elementDesignation.element.styleOverrides?.borderTopWidth ?? null
        );
      },
    }) ?? null;
  const currentBorderRightWidth =
    useOptimisticModifierValue({
      modifierType: { type: "borderWidth", direction: "right", value: 0 },
      extractor: (elementDesignation) => {
        return (
          elementDesignation.element.styleOverrides?.borderRightWidth ?? null
        );
      },
    }) ?? null;
  const currentBorderBottomWidth =
    useOptimisticModifierValue({
      modifierType: { type: "borderWidth", direction: "bottom", value: 0 },
      extractor: (elementDesignation) => {
        return (
          elementDesignation.element.styleOverrides?.borderBottomWidth ?? null
        );
      },
    }) ?? null;
  const currentBorderLeftWidth =
    useOptimisticModifierValue({
      modifierType: { type: "borderWidth", direction: "left", value: 0 },
      extractor: (elementDesignation) => {
        return (
          elementDesignation.element.styleOverrides?.borderLeftWidth ?? null
        );
      },
    }) ?? null;

  // NOTE (Kevin, 2025-12-19, REPL-21177): Track current border color and style to avoid overwriting existing overrides
  const currentBorderColor = useOptimisticModifierValue({
    modifierType: { type: "borderColor", value: "" },
    extractor: (elementDesignation) => {
      return elementDesignation.element.styleOverrides?.borderTopColor ?? null;
    },
  });
  const currentBorderStyle = useOptimisticModifierValue({
    modifierType: { type: "borderStyle", value: "solid" },
    extractor: (elementDesignation) => {
      return elementDesignation.element.styleOverrides?.borderTopStyle ?? null;
    },
  });

  const handleColorChange = React.useCallback(
    (modifier: BorderColorModifierValue) => {
      onChange([modifier]);
    },
    [onChange],
  );

  const handleStyleChange = React.useCallback(
    (modifier: BorderStyleModifierValue) => {
      onChange([modifier]);
    },
    [onChange],
  );

  const handleWidthChange = React.useCallback(
    (modifiers: BorderWidthModifierValue[]) => {
      // NOTE (Kevin, 2025-12-19, REPL-21177): Check if this is a 0→non-zero transition.
      // If all current widths are 0/null and new widths are non-zero, we need to also
      // send color and style modifiers with __COMPUTE__ so the iframe can resolve them.
      // But only for properties that don't already have overrides set.
      const allCurrentWidthsZero = [
        currentBorderTopWidth,
        currentBorderRightWidth,
        currentBorderBottomWidth,
        currentBorderLeftWidth,
      ].every((w) => w === null || w === 0);

      const hasNonZeroNewWidth = modifiers.some(
        (m) => m.value !== null && m.value !== 0,
      );

      if (allCurrentWidthsZero && hasNonZeroNewWidth) {
        // NOTE (Kevin, 2025-12-19, REPL-21177): Transitioning from no border to having border.
        // Only send __COMPUTE__ for properties that don't already have overrides.
        // This prevents overwriting user-set color/style values.
        const additionalModifiers: (
          | BorderColorModifierValue
          | BorderStyleModifierValue
        )[] = [];

        // NOTE (Kevin, 2025-12-19, REPL-21177): Only request computed color if no visible override exists.
        // "transparent" is effectively no color, so we should resolve from Tailwind.
        const hasVisibleColorOverride =
          currentBorderColor && currentBorderColor !== "transparent";
        if (!hasVisibleColorOverride) {
          additionalModifiers.push({
            type: "borderColor",
            value: COMPUTE_VALUE,
          });
        }

        // NOTE (Kevin, 2025-12-19, REPL-21177): For border-style, send "solid" directly instead of __COMPUTE__.
        // If Tailwind has "none", we don't want that anyway. Using a direct value ensures the style
        // is applied in the same server request as the width, avoiding a flicker to "none".
        const hasVisibleStyleOverride =
          currentBorderStyle && currentBorderStyle !== "none";
        if (!hasVisibleStyleOverride) {
          additionalModifiers.push({
            type: "borderStyle",
            value: "solid",
          });
        }

        if (additionalModifiers.length > 0) {
          onChange([...modifiers, ...additionalModifiers]);
        } else {
          onChange(modifiers);
        }
      } else {
        onChange(modifiers);
      }
    },
    [
      onChange,
      currentBorderTopWidth,
      currentBorderRightWidth,
      currentBorderBottomWidth,
      currentBorderLeftWidth,
      currentBorderColor,
      currentBorderStyle,
    ],
  );

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2 items-start">
        <BorderColorModifier onChange={handleColorChange} />
        <BorderStyleModifier onChange={handleStyleChange} />
      </div>
      <BorderWidthModifier onChange={handleWidthChange} />
    </div>
  );
};

export default BorderModifier;
